import { GroupCondition } from '@/api/enums';
import { equal, group, lower, nested } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts/types';
import { CourseStatusAttemptEnum } from '@/client/courses/types';
import {
  DashboardFilterType,
  DashboardFilterValue,
  DataRange,
} from '@/client/reports';
import { Actions, Subjects } from '@/client/users';
import {
  PHISHING_DASHBOARD_VIEW,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types/locales.type';
import { BlockEmailsBanner } from '@/components/block-emails';
import { AwarenessAnalysis } from '@/components/courses';
import { AwarenessKeyResults } from '@/components/courses/AwarenessKeyResults';
import { CompletionsOverTime } from '@/components/courses/CompletionsOverTime';
import { CourseScoreByTag } from '@/components/courses/CourseScoreByTag';
import { NeverLoggedInUsers } from '@/components/courses/NeverLoggedInUsers';
import { ProductActivityOverTime } from '@/components/courses/ProductActivityOverTime';
import { useUsersAwarenessReport } from '@/hooks/query/accounts.hooks';
import { useAppSelector } from '@/hooks/store';
import { useAccordionState } from '@/hooks/useAccordionState';
import { usePermission } from '@/hooks/usePermission';
import awtAnalyticsBlankPageImg from '@/images/blank-pages/awt-analytics-blank-page.svg';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { AccordionTabHeader } from '@/ui/accordion';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { dataRangeOptions, showBlockEmailsMessage } from '@/utils/helpers';
import { AccordionTab, AccordionTabChangeEvent } from 'primereact/accordion';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)`
  flex-wrap: wrap;

  > div {
    flex: 1 1 400px;
    min-width: 0;
    max-width: 100%;
  }
`;

const pathItems = (t: TranslationFunctionType, account: Account) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('sidebar.awareness'),
    className: 'active',
    url: RedirectPaths[RedirectPathsEnum.COURSES](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('sidebar.analytics'),
    url: RedirectPaths[RedirectPathsEnum.CONTENT_ANALYTICS](),
    template: AppBreadCrumbTemplate,
  },
];

export const AWTAnalyticsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { can } = usePermission();
  const activeIndexesKey = `${currentAccount?.id}-${currentUser?.id}`;
  const defaultIndexes = [0, 1, 2];

  const EmptyAnalyticsFilters = useMemo(
    () => [
      group(GroupCondition.OR, [
        equal('status', CourseStatusAttemptEnum.COMPLETED),
        equal('status', CourseStatusAttemptEnum.FORCE_COMPLETED),
        group(GroupCondition.AND, [
          nested('bestAttempt', [equal('completed', null)]),
          lower('dueDate', new Date()),
        ]),
      ]),
    ],
    [],
  );

  const {
    isLoading: isEmptyAnalyticsLoading,
    usersAwarenessReport: emptyAnalyticsUsersAwarenessReport,
  } = useUsersAwarenessReport(currentAccount?.id || '', {
    take: 0,
    skip: 0,
    filters: EmptyAnalyticsFilters,
  });

  const { activeIndex, handleActiveIndexChange: baseHandleActiveIndexChange } =
    useAccordionState({
      storageKey: PHISHING_DASHBOARD_VIEW,
      userKey: activeIndexesKey,
      defaultValue: defaultIndexes,
      multiple: true,
    });
  // const [dataRange, setDataRange] = useState<DataRange>(dataRangeOptions(t)[0]);
  const [filterData] = useState<DashboardFilterValue>({
    type: DashboardFilterType.ALL_DATA,
    managers: [],
    departments: [],
    branches: [],
    groups: [],
  });

  const getFilterData = (
    filterData: DashboardFilterValue,
    dataRange: DataRange,
  ) => {
    return {
      ...dataRange.value,
      managers: filterData.managers?.map((manager) => manager.name),
      departments: filterData.departments?.map((department) => department.name),
      branches: filterData.branches?.map((branch) => branch.id),
      groups: filterData.groups?.map((group) => group.id),
    };
  };

  const hasBlockEmailsBanner = showBlockEmailsMessage(currentAccount);

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      <AppBreadCrumb model={pathItems(t, currentAccount as Account)} />
      <h1 className="mb-4">{t('awareness.analytics.title')}</h1>

      {!emptyAnalyticsUsersAwarenessReport?.count &&
      !isEmptyAnalyticsLoading ? (
        <BlankPage
          isLoading={isEmptyAnalyticsLoading}
          image={awtAnalyticsBlankPageImg}
          title={t('awareness.analytics.blank.heading')}
          description={t('awareness.analytics.blank.description')}
          mainCtaText={t('awareness.analytics.blank.cta')}
          mainCtaOnClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_PLANNER]())
          }
          additionsText={[
            t('awareness.analytics.blank.additions.1'),
            t('awareness.analytics.blank.additions.2'),
            t('awareness.analytics.blank.additions.3'),
          ]}
        />
      ) : (
        <>
          <AwarenessKeyResults />
          {/* <AnalyticsHeader
        dataRange={dataRange}
        setDataRange={setDataRange}
        filterData={filterData}
        setFilterData={setFilterData}
      /> */}
          <AppAccordion
            id="awareness-training-analytics"
            activeIndex={activeIndex}
            onTabChange={(e: AccordionTabChangeEvent) =>
              baseHandleActiveIndexChange(e)
            }
            multiple
          >
            <AccordionTab
              tabIndex={0}
              headerTemplate={
                <AccordionTabHeader
                  title={t('awareness.analytics.scoreByTopic.title')}
                  description={t(
                    'awareness.analytics.scoreByTopic.description',
                  )}
                />
              }
            >
              <CourseScoreByTag
                filters={getFilterData(filterData, dataRangeOptions(t)[1])}
              />
            </AccordionTab>

            <AccordionTab
              tabIndex={1}
              headerTemplate={
                <AccordionTabHeader
                  title={t('awareness.analytics.activityOverTime.title')}
                  description={t(
                    'awareness.analytics.activityOverTime.description',
                  )}
                />
              }
            >
              <StyledContainer
                id="awareness-training-container"
                wrap="wrap"
                align="stretch"
                justify="space-between"
                gap="var(--small-padding)"
                className="mt-4 w-full"
              >
                <ProductActivityOverTime
                  filters={getFilterData(filterData, dataRangeOptions(t)[0])}
                  activeAwarenessTraining
                />
                <CompletionsOverTime
                  filters={getFilterData(filterData, dataRangeOptions(t)[0])}
                />
              </StyledContainer>
              {can(Actions.READ, Subjects.USERS) && <NeverLoggedInUsers />}
            </AccordionTab>

            <AccordionTab
              tabIndex={2}
              headerTemplate={
                <AccordionTabHeader
                  title={t('awareness.analytics.trainingProgress.title')}
                  description={t(
                    'awareness.analytics.trainingProgress.description',
                  )}
                />
              }
            >
              <AwarenessAnalysis
                filters={getFilterData(filterData, dataRangeOptions(t)[0])}
              />
            </AccordionTab>
          </AppAccordion>
        </>
      )}
    </FlexContainer>
  );
};
